import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { datadogLogs } from '@datadog/browser-logs';

import {
  CHECKR_BFF_API_BASE,
  DECAGON_JS_SUPPORT_SCRIPT,
  DECAGON_SCRIPT_ID,
  SESSION_STORAGE_TOKEN_KEY,
} from '../../../constants';
import useAuthGateState from '../../../hooks/useAuthGateState';
import { useChatMetadata } from './chatMetadata';
import {
  hideDecagonForLogout,
  restoreChatAfterLogout,
} from './decagonLogoutVisibility';

type DecagonAuth = {
  user_id: string;
  signature: string;
  epoch: number;
  candidate_id?: string;
};

const isDecagonAuth = (value: unknown): value is DecagonAuth => {
  if (!value || typeof value !== 'object') return false;
  const auth = value as Record<string, unknown>;
  return (
    typeof auth.user_id === 'string' &&
    typeof auth.signature === 'string' &&
    typeof auth.epoch === 'number' &&
    (auth.candidate_id === undefined || typeof auth.candidate_id === 'string')
  );
};

const LEGACY_USER_ID_PREFIX = 'cp:';

// The BFF sends candidate_id alongside the signed user_id. Older responses
// signed `cp:<candidate id>` instead, so only that shape can be unwrapped; the
// current user_id is the candidate email and must never be sent as an id.
const candidateIdFor = (auth: DecagonAuth): string | null => {
  if (auth.candidate_id) return auth.candidate_id;
  if (auth.user_id.startsWith(LEGACY_USER_ID_PREFIX)) {
    return auth.user_id.slice(LEGACY_USER_ID_PREFIX.length) || null;
  }
  datadogLogs.logger.warn('[CP] Decagon auth response has no candidate_id');
  return null;
};

export const loadDecagon = () => {
  if (document.getElementById(DECAGON_SCRIPT_ID)) return;

  const script = document.createElement('script');
  script.src = DECAGON_JS_SUPPORT_SCRIPT;
  script.id = DECAGON_SCRIPT_ID;
  script.dataset.testid = DECAGON_SCRIPT_ID;
  document.body.appendChild(script);
};

type ChatMetadata = Record<string, string | null>;

const decagonMetadata = (
  metadata: ChatMetadata,
  reportCount: number,
  candidateId?: string,
): ChatMetadata => ({
  ...metadata,
  pathname: globalThis.location.pathname,
  persona: 'candidate',
  report_count: String(reportCount),
  ...(candidateId ? { candidate_id: candidateId } : {}),
});

const CONVERSATION_EVENTS = new Set([
  'DECAGON_NEW_CONVERSATION',
  'CONVERSATION_LOADED',
]);

const authenticateDecagon = async (
  token: string,
  isCancelled: () => boolean,
  metadataFor: (auth: DecagonAuth) => ChatMetadata,
) => {
  const response = await fetch(
    `${CHECKR_BFF_API_BASE}/chat/auth?token=${encodeURIComponent(token)}`,
  );
  if (!response.ok) throw new Error(`Decagon auth failed: ${response.status}`);

  const auth: unknown = await response.json();
  if (!isDecagonAuth(auth)) throw new Error('Decagon auth response is invalid');
  // Chat was disabled or the token changed while the request was in flight;
  // publishing this identity would undo the effect cleanup.
  if (isCancelled()) return null;

  const metadata = metadataFor(auth);
  globalThis.duet.setUserId(auth.user_id);
  globalThis.duet.setUserIdAuth(auth.signature, auth.epoch);
  globalThis.duet.setMetadata(metadata);
  globalThis.duet.setChatSessionDuration(4);
  return metadata;
};

export const useDecagonChatbot = (enabled: boolean, reportCount: number) => {
  const portalToken = useSelector((state: any) => state.portal?.token);
  // Auth state comes from the app's auth source (ECDEX-3441), not the
  // `portal` slice. `loggedOut` drives the logout toast and is only reset
  // when the toast closes, so it can still be true after the candidate signs
  // back in. `loggedIn` is set by a successful sign-in and reset by logout.
  const { portalAuth } = useAuthGateState();
  const signedOut = Boolean(portalAuth.loggedOut) && !portalAuth.loggedIn;
  const chatMetadata = useChatMetadata();
  const chatMetadataRef = useRef(chatMetadata);
  chatMetadataRef.current = chatMetadata;
  const reportCountRef = useRef(reportCount);
  reportCountRef.current = reportCount;
  const userIdRef = useRef<string | null>(null);
  const candidateIdRef = useRef<string | null>(null);
  const metadataRef = useRef<ChatMetadata | null>(null);
  const listenerAttached = useRef(false);

  // The access token can be refreshed after the metadata was memoized, so
  // read it from sessionStorage every time a payload is sent.
  const compose = () =>
    decagonMetadata(
      {
        ...chatMetadataRef.current,
        cxUserAccessToken: globalThis.sessionStorage.getItem(
          SESSION_STORAGE_TOKEN_KEY,
        ),
      },
      reportCountRef.current,
      candidateIdRef.current ?? undefined,
    );

  useEffect(() => {
    if (!enabled || !userIdRef.current) return;
    const metadata = compose();
    metadataRef.current = metadata;
    if (typeof globalThis.duet?.setMetadata === 'function') {
      globalThis.duet.setMetadata(metadata);
    }
  }, [enabled, chatMetadata, reportCount]);

  useEffect(() => {
    if (!enabled) return undefined;

    // Logout clears the Redux token but can leave the session token in
    // place. Authenticating with that leftover token would put the widget
    // back on the sign-in screen.
    if (signedOut) {
      hideDecagonForLogout();
      return undefined;
    }

    loadDecagon();

    let attempts = 0;
    let cancelled = false;
    let poll: ReturnType<typeof setInterval> | undefined;

    const setup = () => {
      if (cancelled || typeof globalThis.duet?.open !== 'function')
        return false;

      if (
        !listenerAttached.current &&
        typeof globalThis.duet.setEventListener === 'function'
      ) {
        listenerAttached.current = true;
        // /conversation/new writes its response metadata over ours and
        // defaults persona to customer. setMetadata again drops that overlay.
        globalThis.duet.setEventListener(payload => {
          if (
            !payload?.eventName ||
            !CONVERSATION_EVENTS.has(payload.eventName)
          )
            return;
          // The listener outlives the effect; after chat is disabled or the
          // token changes there is no identity to republish.
          if (!userIdRef.current || !metadataRef.current) return;
          const metadata = compose();
          metadataRef.current = metadata;
          globalThis.duet.setMetadata(metadata);
        });
      }

      const token =
        portalToken ||
        globalThis.sessionStorage.getItem(SESSION_STORAGE_TOKEN_KEY);
      if (token) {
        authenticateDecagon(
          token,
          () => cancelled,
          auth => {
            userIdRef.current = auth.user_id;
            candidateIdRef.current = candidateIdFor(auth);
            const metadata = compose();
            metadataRef.current = metadata;
            return metadata;
          },
        )
          .then(metadata => {
            // Only a successful, current sign-in undoes a logout hide; a
            // failed or cancelled auth leaves the launcher off the sign-in
            // screen.
            if (metadata) restoreChatAfterLogout();
          })
          .catch(() => {});
      }
      return true;
    };

    if (!setup()) {
      poll = setInterval(() => {
        attempts += 1;
        if (setup() || attempts >= 100) {
          clearInterval(poll);
        }
      }, 50);
    }

    return () => {
      cancelled = true;
      userIdRef.current = null;
      candidateIdRef.current = null;
      metadataRef.current = null;
      if (poll) clearInterval(poll);
    };
  }, [enabled, portalToken, reportCount, signedOut]);
};
